import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import PageHeader from "@/components/marketing/PageHeader";
import Reveal from "@/components/Reveal";
import { SERVICES, MARKETS } from "@/data/content";
import { BTN_PRIMARY, BTN_OUTLINE_DARK } from "@/lib/ui";
import { trackEvent } from "@/lib/tracking";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    if (svc) trackEvent("page_view", { label: `service:${svc.title}` });
  }, [svc]);

  if (!svc) return <Navigate to="/services/heavy-highway-infrastructure" replace />;

  const others = SERVICES.filter((s) => s.slug !== svc.slug);

  return (
    <div data-testid="service-detail-page">
      <PageHeader overline="Service" crumb={svc.title} title={svc.title} subtitle={svc.short} image={svc.img} />

      <section className="bg-rc-bg py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <div className="overflow-hidden rounded-2xl">
              <img src={svc.img} alt={svc.title} className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="text-rc-brown text-lg leading-relaxed">{svc.desc}</p>
            </Reveal>
            <Reveal delay={0.1} className="mt-8 grid sm:grid-cols-2 gap-3">
              {svc.bullets.map((b) => (
                <div key={b} className="flex items-center gap-3 bg-white border border-black/10 rounded-xl px-4 py-3">
                  <span className="w-7 h-7 rounded-full bg-rc-red/10 text-rc-red flex items-center justify-center shrink-0">
                    <Check size={15} />
                  </span>
                  <span className="text-rc-charcoal text-sm font-medium">{b}</span>
                </div>
              ))}
            </Reveal>
            <Reveal delay={0.15} className="mt-8 flex flex-wrap gap-3">
              <Link to={svc.ctaTo || "/contact"} data-testid="service-detail-contact" className={BTN_PRIMARY}>
                {svc.ctaLabel || "Contact Us"} <ArrowRight size={17} />
              </Link>
              <Link to="/services/heavy-highway-infrastructure" className={BTN_OUTLINE_DARK}>
                Explore Services
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-rc-bg pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="font-mono text-rc-red text-xs uppercase tracking-[0.3em] mb-6">Markets Served</div>
          <div className="flex flex-wrap gap-3 mb-16">
            {MARKETS.map((m) => (
              <span key={m} className="font-mono text-xs uppercase tracking-[0.15em] text-rc-brown border border-black/15 px-4 py-2 rounded-full">
                {m}
              </span>
            ))}
          </div>

          <h3 className="font-heading font-black text-rc-blue text-2xl mb-6">Explore other capabilities</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={o.cta || `/services/${o.slug}`}
                data-testid={`related-service-${o.key}`}
                className="group relative overflow-hidden rounded-2xl h-44"
              >
                <img src={o.img} alt={o.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-rc-charcoal to-transparent group-hover:from-rc-red/90 transition-colors" />
                <div className="absolute bottom-0 p-4 flex items-end justify-between w-full">
                  <h4 className="font-heading font-bold text-white text-base leading-tight">{o.title}</h4>
                  <ArrowUpRight size={18} className="text-white" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
